import type { Connector, DesktopWorkerStatus, ISODate } from '@app/shared'
import clsx from 'clsx'

import { MobileSheet } from '../shell/MobileSheet'
import { reportRows, sinceWhen, syncSummary } from './day'
import { Glyph } from './glyphs'

// ============================================================================
// BOARD 21 · THE SYNC REPORT — what a pull to sync (or a tap on the status chip) found, one row
// per connector in that connector's own words. A fine connector is a ground row with an OK tile;
// one with a problem is a slab row with the accent WARN tile. Nothing here is a button but Done:
// every problem row already says whether there is anything to do.
// ============================================================================

export function SyncSheet({
  open,
  onClose,
  connectors,
  connectorsError,
  worker,
  refreshError,
  today,
  onNotifications,
}: {
  open: boolean
  onClose: () => void
  connectors: Connector[] | undefined
  connectorsError: unknown
  worker: DesktopWorkerStatus | undefined
  /** What the pull's own sync calls refused with, in their words. */
  refreshError: string | null
  today: ISODate | undefined
  /**
   * Open the notifications sheet. NOT ON BOARD 21: with no unread notification the status chip
   * reads "Synced" and opens this report, so without this the notifications sheet — and the gear
   * to the phone's notification settings — would be unreachable on a quiet day.
   */
  onNotifications: () => void
}) {
  const rows = connectors ? reportRows(connectors, worker, today) : []
  const latest = syncSummary(connectors).latest
  const fine = rows.filter((r) => r.ok).length

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      label="Sync report"
      panelStyle={{ borderRadius: '30px 30px 0 0', background: 'var(--m-card)' }}
      bodyStyle={{ padding: '4px 18px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div className="flex items-baseline justify-between gap-3 px-1">
        <span className="text-[26px] font-extrabold">{latest ? `Synced ${sinceWhen(latest, today)}` : 'Not synced yet'}</span>
        {rows.length > 0 && (
          <span className="flex-none text-[13px] font-semibold text-m-sub">
            {fine} of {rows.length} up to date
          </span>
        )}
      </div>

      {refreshError && <p className="px-1 text-[12.5px] font-semibold leading-[1.45] text-m-sub">{refreshError}</p>}

      {!connectors && (
        <p className="px-1 text-[13.5px] font-semibold leading-[1.45]">
          The connector list could not be read, so this phone cannot say what is current.{' '}
          {connectorsError instanceof Error ? connectorsError.message : ''}
        </p>
      )}

      {rows.map((r) => (
        <div
          key={r.key}
          className={clsx(
            'box-border flex min-h-[62px] items-center gap-3 rounded-[20px] px-[14px] py-2',
            r.ok ? 'bg-m-ground text-m-ink' : 'bg-m-slab text-m-on-slab',
          )}
        >
          <span
            className={clsx(
              'flex size-[38px] flex-none items-center justify-center rounded-[13px]',
              r.ok ? 'bg-m-card text-m-ink' : 'bg-m-accent text-m-on-accent',
            )}
          >
            <Glyph name={r.ok ? 'check' : 'warn'} size={18} strokeWidth={2.3} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
            <span className="text-[15.5px] font-extrabold">{r.name}</span>
            <span className={clsx('text-[12.5px] font-semibold leading-[1.4]', r.ok ? 'text-m-sub' : 'text-m-on-slab-dim')}>
              {r.line}
            </span>
          </span>
        </div>
      ))}

      <button
        type="button"
        onClick={onNotifications}
        className="m-tap flex h-11 items-center gap-2 self-start px-1 text-[13.5px] font-bold text-m-sub"
      >
        <Glyph name="bell" size={15} />
        Notifications
      </button>

      <button
        type="button"
        onClick={onClose}
        className="m-tap h-[54px] flex-none rounded-[20px] bg-m-slab text-[16px] font-extrabold text-m-on-slab"
      >
        Done
      </button>
    </MobileSheet>
  )
}
